import React, { useState, useEffect, useContext, useCallback } from "react";
import "./Login.css";
import { MyContext } from "../../../../Context/MyContext";
import API from "../../../../Api/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { resolveLoginRedirect } from "../../../../utils/resolveLoginRedirect";
import { persistUserSession } from "../../../../utils/authSession";
import { startPresence } from "../../../../Services/presenceService";
import OtpModal from "./OtpModal";
import AppIcon from "../../../../Components/AppIcon";

const DELETED_MSG = "Your account has been deleted. Please create a new account.";

const isAccountDeletedError = (err) => {
  const code = err?.response?.data?.code;
  const msg = err?.response?.data?.error || "";
  return code === "ACCOUNT_DELETED" || String(msg).toLowerCase().includes("account has been deleted");
};

const Login = ({ setActiveTab }) => {
  const { logininput, setUser } = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const safeLogin    = logininput?.login || [];
  const otpField     = safeLogin.find(i => i.type === "otp");
  const showOtpLogin = otpField?._show;

  const [formData,     setFormData]     = useState({ phone_or_email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [otpModal,     setOtpModal]     = useState(false);
  const [otpPin,       setOtpPin]       = useState("");
  const [sending,      setSending]      = useState(false);
  const [loggingIn,    setLoggingIn]    = useState(false);
  const [verified,     setVerified]     = useState({ login: false });
  const [deletedPopup, setDeletedPopup] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleAuthError = (err, fallback = "Login failed") => {
    if (isAccountDeletedError(err)) {
      setDeletedPopup(true);
      return;
    }
    toast.error(err.response?.data?.error || fallback);
  };

  const completeLogin = useCallback((userData) => {
    try {
      const normalized = persistUserSession(userData);
      setUser(normalized);
      startPresence();
      const target = resolveLoginRedirect(location, "/");
      navigate(target, { replace: true });
    } catch {
      toast.error("Login failed: invalid server response. Please try again.");
    }
  }, [location, navigate, setUser]);

  const handleLoginViaOtp = async () => {
    if (!formData.phone_or_email) { toast.error("Enter email first!"); return; }
    const isEmail = formData.phone_or_email.includes("@");
    if (!isEmail) { toast.error("OTP login requires email."); return; }
    setSending(true);
    try {
      const res = await API.post('/auth/send-otp', {
        email:   formData.phone_or_email,
        purpose: 'login',
      });
      if (res.data.otp_dev) {
        setOtpPin(res.data.otp_dev);
        toast.info(`Dev OTP: ${res.data.otp_dev}`);
      } else {
        setOtpPin("");
        toast.success("OTP sent to your email!");
      }
      setOtpModal(true);
    } catch (err) {
      handleAuthError(err, "Failed to send OTP");
    } finally { setSending(false); }
  };

  useEffect(() => {
    if (!verified.login) return;
    const doLogin = async () => {
      try {
        const res = await API.post('/auth/login-otp', {
          email_or_phone: formData.phone_or_email,
          otp:            otpPin || "verified",
          platform:       'web',
        });
        toast.success("Login Successful!");
        completeLogin(res.data);
      } catch (err) {
        handleAuthError(err, "OTP login failed");
      }
    };
    doLogin();
  }, [verified.login]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.phone_or_email) { toast.error("Phone or Email required!"); return; }
    if (!formData.password)       { toast.error("Password required!"); return; }
    setLoggingIn(true);
    try {
      const res = await API.post('/auth/login', {
        email_or_phone: formData.phone_or_email,
        password:       formData.password,
        platform:       'web',
      });
      toast.success("Login Successful!");
      completeLogin(res.data);
    } catch (err) {
      handleAuthError(err, "Login failed");
    } finally { setLoggingIn(false); }
  };

  const resendOtp = async () => {
    setSending(true);
    try {
      const res = await API.post('/auth/send-otp', {
        email:   formData.phone_or_email,
        purpose: 'login',
      });
      if (res.data.otp_dev) { setOtpPin(res.data.otp_dev); toast.info(`Dev OTP: ${res.data.otp_dev}`); }
      else { setOtpPin(""); toast.success("OTP resent!"); }
    } catch (err) {
      handleAuthError(err, "Resend failed");
    }
    finally { setSending(false); }
  };

  return (
    <div className="login-container">
      <div className="login-brand"><span className="login-brand-name">Properties Infinite</span></div>
      <h2 className="login-title">Login</h2>
      <form className="login-form" onSubmit={handleSubmit}>
        {safeLogin.map((item) => {
          if (item.type === "otp") return null;
          return (
            <React.Fragment key={item.id}>
              <div className="input-group">
                <label>{item.name}</label>
                <div className="input-wrapper">
                  <input
                    type={item.type === "password" ? (showPassword ? "text" : "password") : item.type}
                    name={item.field} value={formData[item.field] || ""}
                    onChange={handleChange} placeholder={item.name}
                  />
                  {item.type === "password" && item.is_show && (
                    <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  )}
                </div>
              </div>
              {item.field === "password" && (
                <>
                  <div className="login-links">
                    <span className="forgot-link" onClick={() => setActiveTab("forgot_password")}>Forgot Password?</span>
                  </div>
                  {showOtpLogin && <div className="or-divider"><span>OR</span></div>}
                </>
              )}
            </React.Fragment>
          );
        })}

        {showOtpLogin && (
          <button type="button"
            className={`login-btn ${verified.login ? "otp-success" : ""}`}
            onClick={handleLoginViaOtp}
            disabled={verified.login || sending}
            style={{ marginBottom: "10px" }}
          >
            {sending ? "Sending OTP…" : verified.login ? <>OTP Verified <AppIcon name="check" size={14} /></> : "Login via OTP"}
          </button>
        )}

        <button type="submit" className="login-btn" disabled={loggingIn}>
          {loggingIn ? "Logging in…" : "Login"}
        </button>
        <p className="signup-text">
          Don't have an account? <span onClick={() => setActiveTab("signup")}>Sign up</span>
        </p>
      </form>

      <OtpModal
        otpModal={otpModal}
        setOtpModal={setOtpModal}
        otpType="login"
        otpPin={otpPin}
        setVerified={setVerified}
        email={formData.phone_or_email}
        onResend={resendOtp}
      />

      {deletedPopup && (
        <div className="account-deleted-overlay" role="dialog" aria-modal="true">
          <div className="account-deleted-modal">
            <h3>Account Deleted</h3>
            <p>{DELETED_MSG}</p>
            <div className="account-deleted-actions">
              <button
                type="button"
                className="login-btn"
                onClick={() => {
                  setDeletedPopup(false);
                  setActiveTab("signup");
                }}
              >
                Create new account
              </button>
              <button
                type="button"
                className="account-deleted-dismiss"
                onClick={() => setDeletedPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
